import {
    AccountType,
    DefaultCashBasisAccountCodes,
} from "@common/constants/domains/accounting/accounts";

export enum Form1099Box {
    NEC_BOX_1_NONEMPLOYEE_COMPENSATION = "nec_box_1_nonemployee_compensation",
    NEC_BOX_2_DIRECT_SALES = "nec_box_2_direct_sales",
    NEC_BOX_3_EXCESS_GOLDEN_PARACHUTE = "nec_box_3_excess_golden_parachute",
    NEC_BOX_4_FEDERAL_TAX_WITHHELD = "nec_box_4_federal_tax_withheld",
    MISC_BOX_1_RENTS = "misc_box_1_rents",
    MISC_BOX_2_ROYALTIES = "misc_box_2_royalties",
    MISC_BOX_3_OTHER_INCOME = "misc_box_3_other_income",
    MISC_BOX_4_FEDERAL_TAX_WITHHELD = "misc_box_4_federal_tax_withheld",
    MISC_BOX_5_FISHING_BOAT_PROCEEDS = "misc_box_5_fishing_boat_proceeds",
    MISC_BOX_6_MEDICAL_PAYMENTS = "misc_box_6_medical_payments",
    MISC_BOX_7_DIRECT_SALES = "misc_box_7_direct_sales",
    MISC_BOX_8_SUBSTITUTE_PAYMENTS = "misc_box_8_substitute_payments",
    MISC_BOX_9_CROP_INSURANCE_PROCEEDS = "misc_box_9_crop_insurance_proceeds",
    MISC_BOX_10_GROSS_PROCEEDS_ATTORNEY = "misc_box_10_gross_proceeds_attorney",
    MISC_BOX_11_FISH_PURCHASED_FOR_RESALE = "misc_box_11_fish_purchased_for_resale",
}

export const FORM_1099_BOX_SOURCES = ["inferred", "user"] as const;

export type Form1099BoxSource = (typeof FORM_1099_BOX_SOURCES)[number];

export enum Form1099Type {
    FORM_1099_NEC = "1099-NEC",
    FORM_1099_MISC = "1099-MISC",
}

export interface Form1099BoxOption {
    value: Form1099Box;
    form: Form1099Type;
    boxNumber: string;
    boxDescription: string;
    label: string;
}

export interface Form1099BoxGroup {
    form: Form1099Type;
    options: readonly Form1099BoxOption[];
}

export const FORM_1099_NEC_BOX_OPTIONS: readonly Form1099BoxOption[] = [
    {
        value: Form1099Box.NEC_BOX_1_NONEMPLOYEE_COMPENSATION,
        form: Form1099Type.FORM_1099_NEC,
        boxNumber: "Box 1",
        boxDescription: "Non-employee compensation",
        label: "1099-NEC: Box 1",
    },
    {
        value: Form1099Box.NEC_BOX_2_DIRECT_SALES,
        form: Form1099Type.FORM_1099_NEC,
        boxNumber: "Box 2",
        boxDescription: "Direct sales",
        label: "1099-NEC: Box 2",
    },
    {
        value: Form1099Box.NEC_BOX_3_EXCESS_GOLDEN_PARACHUTE,
        form: Form1099Type.FORM_1099_NEC,
        boxNumber: "Box 3",
        boxDescription: "Excess golden parachute",
        label: "1099-NEC: Box 3",
    },
    {
        value: Form1099Box.NEC_BOX_4_FEDERAL_TAX_WITHHELD,
        form: Form1099Type.FORM_1099_NEC,
        boxNumber: "Box 4",
        boxDescription: "Federal tax withheld",
        label: "1099-NEC: Box 4",
    },
];

export const FORM_1099_MISC_BOX_OPTIONS: readonly Form1099BoxOption[] = [
    {
        value: Form1099Box.MISC_BOX_1_RENTS,
        form: Form1099Type.FORM_1099_MISC,
        boxNumber: "Box 1",
        boxDescription: "Rents",
        label: "1099-MISC: Box 1",
    },
    {
        value: Form1099Box.MISC_BOX_2_ROYALTIES,
        form: Form1099Type.FORM_1099_MISC,
        boxNumber: "Box 2",
        boxDescription: "Royalties",
        label: "1099-MISC: Box 2",
    },
    {
        value: Form1099Box.MISC_BOX_3_OTHER_INCOME,
        form: Form1099Type.FORM_1099_MISC,
        boxNumber: "Box 3",
        boxDescription: "Other income",
        label: "1099-MISC: Box 3",
    },
    {
        value: Form1099Box.MISC_BOX_4_FEDERAL_TAX_WITHHELD,
        form: Form1099Type.FORM_1099_MISC,
        boxNumber: "Box 4",
        boxDescription: "Federal tax withheld",
        label: "1099-MISC: Box 4",
    },
    {
        value: Form1099Box.MISC_BOX_5_FISHING_BOAT_PROCEEDS,
        form: Form1099Type.FORM_1099_MISC,
        boxNumber: "Box 5",
        boxDescription: "Fishing boat proceeds",
        label: "1099-MISC: Box 5",
    },
    {
        value: Form1099Box.MISC_BOX_6_MEDICAL_PAYMENTS,
        form: Form1099Type.FORM_1099_MISC,
        boxNumber: "Box 6",
        boxDescription: "Medical payments",
        label: "1099-MISC: Box 6",
    },
    {
        value: Form1099Box.MISC_BOX_7_DIRECT_SALES,
        form: Form1099Type.FORM_1099_MISC,
        boxNumber: "Box 7",
        boxDescription: "Direct sales",
        label: "1099-MISC: Box 7",
    },
    {
        value: Form1099Box.MISC_BOX_8_SUBSTITUTE_PAYMENTS,
        form: Form1099Type.FORM_1099_MISC,
        boxNumber: "Box 8",
        boxDescription: "Substitute payments in lieu of dividends or interest",
        label: "1099-MISC: Box 8",
    },
    {
        value: Form1099Box.MISC_BOX_9_CROP_INSURANCE_PROCEEDS,
        form: Form1099Type.FORM_1099_MISC,
        boxNumber: "Box 9",
        boxDescription: "Crop insurance proceeds",
        label: "1099-MISC: Box 9",
    },
    {
        value: Form1099Box.MISC_BOX_10_GROSS_PROCEEDS_ATTORNEY,
        form: Form1099Type.FORM_1099_MISC,
        boxNumber: "Box 10",
        boxDescription: "Gross proceeds paid to an attorney",
        label: "1099-MISC: Box 10",
    },
    {
        value: Form1099Box.MISC_BOX_11_FISH_PURCHASED_FOR_RESALE,
        form: Form1099Type.FORM_1099_MISC,
        boxNumber: "Box 11",
        boxDescription: "Fish purchased for resale",
        label: "1099-MISC: Box 11",
    },
];

export const FORM_1099_BOX_GROUPS: readonly Form1099BoxGroup[] = [
    {
        form: Form1099Type.FORM_1099_NEC,
        options: FORM_1099_NEC_BOX_OPTIONS,
    },
    {
        form: Form1099Type.FORM_1099_MISC,
        options: FORM_1099_MISC_BOX_OPTIONS,
    },
];

export const FORM_1099_BOX_OPTIONS: readonly Form1099BoxOption[] = [
    ...FORM_1099_NEC_BOX_OPTIONS,
    ...FORM_1099_MISC_BOX_OPTIONS,
];

const FORM_1099_BOX_VALUES: ReadonlySet<string> = new Set(
    Object.values(Form1099Box),
);

export function isForm1099Box(value: string): value is Form1099Box {
    return FORM_1099_BOX_VALUES.has(value);
}

const FORM_1099_BOX_LABELS = new Map<Form1099Box, string>(
    FORM_1099_BOX_OPTIONS.map((option) => [option.value, option.label]),
);

export function getForm1099BoxLabel(
    box: Form1099Box | null | undefined,
): string | null {
    if (!box) {
        return null;
    }
    return FORM_1099_BOX_LABELS.get(box) ?? null;
}

export const DEFAULT_FORM_1099_BOX_BY_KICK_ORIGINAL_CODE: ReadonlyMap<
    number,
    Form1099Box
> = new Map<number, Form1099Box>([
    [
        DefaultCashBasisAccountCodes.cogsLabor,
        Form1099Box.NEC_BOX_1_NONEMPLOYEE_COMPENSATION,
    ],
    [
        DefaultCashBasisAccountCodes.contractLabor,
        Form1099Box.NEC_BOX_1_NONEMPLOYEE_COMPENSATION,
    ],
    [
        DefaultCashBasisAccountCodes.commissions,
        Form1099Box.NEC_BOX_1_NONEMPLOYEE_COMPENSATION,
    ],
    [
        DefaultCashBasisAccountCodes.legalAndProfessionalFees,
        Form1099Box.NEC_BOX_1_NONEMPLOYEE_COMPENSATION,
    ],
    [
        DefaultCashBasisAccountCodes.facilitiesRepairsAndMaintenance,
        Form1099Box.NEC_BOX_1_NONEMPLOYEE_COMPENSATION,
    ],
    [
        DefaultCashBasisAccountCodes.facilitiesCleaningFees,
        Form1099Box.NEC_BOX_1_NONEMPLOYEE_COMPENSATION,
    ],
    [
        DefaultCashBasisAccountCodes.facilitiesManagementFees,
        Form1099Box.NEC_BOX_1_NONEMPLOYEE_COMPENSATION,
    ],
    [DefaultCashBasisAccountCodes.rentAndLease, Form1099Box.MISC_BOX_1_RENTS],
    [DefaultCashBasisAccountCodes.vehicleLeases, Form1099Box.MISC_BOX_1_RENTS],
]);

export function getKickDefaultForm1099Box(
    kickOriginalCode: number | null | undefined,
): Form1099Box | null {
    if (kickOriginalCode == null) {
        return null;
    }
    return (
        DEFAULT_FORM_1099_BOX_BY_KICK_ORIGINAL_CODE.get(kickOriginalCode) ??
        null
    );
}

const ALL_ACCOUNTS_ELIGIBLE_TYPES: ReadonlySet<AccountType> = new Set([
    AccountType.cogs,
    AccountType.operatingExpenses,
    AccountType.otherExpenses,
]);

const ELIGIBLE_CUSTOM_ACCOUNT_TYPES: ReadonlySet<AccountType> = new Set([
    AccountType.otherCurrentAssets,
    AccountType.fixedAssets,
    AccountType.intangibleAssets,
    AccountType.otherAssets,
    AccountType.otherCurrentLiabilities,
    AccountType.otherLiabilities,
]);

export interface Form1099BoxEligibilityAccount {
    type: AccountType;
    isManuallyCreated: boolean;
    financialAccountId: number | null;
    relatedEntityId?: number | null;
}

export function isAccountEligibleForForm1099Box(
    account: Form1099BoxEligibilityAccount,
): boolean {
    if (
        account.financialAccountId !== null ||
        (account.relatedEntityId !== undefined &&
            account.relatedEntityId !== null)
    ) {
        return false;
    }

    if (ALL_ACCOUNTS_ELIGIBLE_TYPES.has(account.type)) {
        return true;
    }

    return (
        account.isManuallyCreated &&
        ELIGIBLE_CUSTOM_ACCOUNT_TYPES.has(account.type)
    );
}

interface ResolveNewAccountForm1099BoxParams {
    account: Form1099BoxEligibilityAccount & {
        kickOriginalCode: number | null;
    };
    sourceBox?: Form1099Box | null;
}

export function resolveNewAccountForm1099Box({
    account,
    sourceBox,
}: ResolveNewAccountForm1099BoxParams): Form1099Box | null {
    if (!isAccountEligibleForForm1099Box(account)) {
        return null;
    }
    if (sourceBox !== undefined) {
        return sourceBox;
    }
    if (account.isManuallyCreated) {
        return null;
    }
    return getKickDefaultForm1099Box(account.kickOriginalCode);
}

interface ResolveForm1099BoxUpdateParams {
    requestedBox: Form1099Box | null | undefined;
    currentBox: Form1099Box | null;
    account: Form1099BoxEligibilityAccount;
}

export function resolveForm1099BoxUpdate({
    requestedBox,
    currentBox,
    account,
}: ResolveForm1099BoxUpdateParams): Form1099Box | null | undefined {
    if (requestedBox !== undefined) {
        return requestedBox;
    }
    if (currentBox !== null && !isAccountEligibleForForm1099Box(account)) {
        return null;
    }
    return undefined;
}

export const FORM_1099_BOX_FILTER_UNASSIGNED = "unassigned";
export const FORM_1099_BOX_FILTER_UNASSIGNED_LABEL = "Unassigned";

export type Form1099BoxFilterValue =
    | Form1099Box
    | typeof FORM_1099_BOX_FILTER_UNASSIGNED;

export function isForm1099BoxFilterValue(
    value: string,
): value is Form1099BoxFilterValue {
    return value === FORM_1099_BOX_FILTER_UNASSIGNED || isForm1099Box(value);
}

interface Form1099BoxFilterAccount extends Form1099BoxEligibilityAccount {
    form1099Box?: Form1099Box | null;
}

// "Unassigned" means eligible for a box but none chosen; ineligible accounts
// show no box in the chart, so they never match it.
export function accountMatchesForm1099BoxFilter(
    account: Form1099BoxFilterAccount,
    selected: readonly Form1099BoxFilterValue[],
): boolean {
    if (account.form1099Box) {
        return selected.includes(account.form1099Box);
    }
    return (
        selected.includes(FORM_1099_BOX_FILTER_UNASSIGNED) &&
        isAccountEligibleForForm1099Box(account)
    );
}
